import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { getMoviesByIds, getReviewsByIds, specials } from '@/lib/data';

export default function Special() {
  const [activeId, setActiveId] = useState(specials[0].id);
  const active = specials.find((s) => s.id === activeId) ?? specials[0];
  const movies = getMoviesByIds(active.movieIds);
  const articles = getReviewsByIds(active.articleIds);

  useEffect(() => {
    document.title = `${active.title} - 影评专题 - 韩国理论片`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute('content', `${active.title}：${active.intro.slice(0, 80)}`);
    }
  }, [active]);

  return (
    <SiteLayout sidebar>
      <Breadcrumb items={[{ label: '影评专题' }]} />

      <header className="mb-6">
        <p className="font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
          Special Topics
        </p>
        <h1 className="mt-2 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
          影评专题
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          按主题脉络串联影片与影评，从阶层、复仇、现实主义到极简影像，帮你在阅读中建立完整的知识框架。
        </p>
      </header>

      {/* 专题切换 */}
      <div className="flex flex-wrap gap-2 mb-6">
        {specials.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveId(s.id)}
            className={`rounded-full px-4 py-2 font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              s.id === activeId
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            {s.title.split('：')[0]}
          </button>
        ))}
      </div>

      {/* 专题主图 + 导语 */}
      <section className="rounded-lg overflow-hidden border border-background-200/80 bg-background-50">
        <div className="relative w-full h-[260px] md:h-[360px] bg-background-200">
          <img
            src={active.cover}
            alt={`${active.title} 专题主图`}
            title={active.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 font-label text-[11px] font-bold uppercase tracking-wider text-primary-950">
              <i className="ri-book-marked-fill" aria-hidden="true" />
              专题 {specials.findIndex((s) => s.id === activeId) + 1} / {specials.length}
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-background-50 leading-tight">
              {active.title}
            </h2>
            <p className="mt-1.5 font-label text-sm text-background-200/85">{active.subtitle}</p>
          </div>
        </div>
        <div className="p-5 md:p-8">
          <p className="text-sm md:text-base leading-loose text-foreground-700">{active.intro}</p>
        </div>
      </section>

      {/* 精选影评 */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-article-line text-accent-600" aria-hidden="true" />
          专题精选影评
        </h2>
        <div className="mt-5 flex flex-col gap-4">
          {articles.map((r) => {
            const movie = movies.find((m) => m.id === r.movieId);
            return (
              <article
                key={r.id}
                className="rounded-lg border border-background-200/80 bg-background-50 p-5 hover:border-primary-300 transition-colors"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-user-3-line" aria-hidden="true" />
                    {r.author}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-calendar-line" aria-hidden="true" />
                    {r.date}
                  </span>
                  {movie && (
                    <span className="inline-flex items-center gap-1 text-primary-600">
                      <i className="ri-film-line" aria-hidden="true" />
                      《{movie.title}》
                    </span>
                  )}
                </div>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{r.excerpt}</p>
                <Link
                  to={`/detail/${r.movieId}`}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  阅读全文
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* 相关影片 */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-film-line text-primary-500" aria-hidden="true" />
          专题相关影片
        </h2>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-5">
          {movies.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}